<template>
  <div class="viq-gap-results">
    <h3 class="viq-gap-results__title">Gap Analysis Results</h3>

    <div v-if="isStreaming" class="viq-gap-results__loading">
      <span class="viq-gap-results__spinner" />
      Analysing gaps...
    </div>

    <template v-if="parsedResults">
      <!-- Summary -->
      <div v-if="parsedResults.summary" class="viq-gap-results__summary">
        <h4>Summary</h4>
        <p>{{ parsedResults.summary }}</p>
      </div>

      <!-- Gap Items -->
      <div v-if="gapItems.length > 0" class="viq-gap-results__items">
        <div
          v-for="(item, i) in gapItems"
          :key="i"
          class="viq-gap-results__item"
        >
          <div class="viq-gap-results__item-header">
            <span
              class="viq-gap-results__severity"
              :class="'viq-gap-results__severity--' + (item.severity || 'medium').toLowerCase()"
            >
              {{ item.severity || 'Medium' }}
            </span>
            <h4 class="viq-gap-results__item-title">{{ item.title || item.gap }}</h4>
          </div>
          <p v-if="item.description" class="viq-gap-results__item-desc">
            {{ item.description }}
          </p>
          <div v-if="item.recommendation" class="viq-gap-results__recommendation">
            <strong>Recommendation:</strong> {{ item.recommendation }}
          </div>
        </div>
      </div>

      <!-- Streaming content fallback -->
      <div
        v-if="parsedResults.content && !gapItems.length"
        class="viq-gap-results__content"
        v-html="renderedContent"
      />
    </template>

    <div v-if="!isStreaming" class="viq-gap-results__actions">
      <el-button type="primary" @click="exportPdf">Export as PDF</el-button>
      <el-button v-if="$config.features && $config.features.viqShare" @click="share">Save &amp; Share</el-button>
      <el-button @click="newAnalysis">New Analysis</el-button>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import DOMPurify from "dompurify";

export default {
  name: "ViqGapResults",
  props: {
    results: {
      type: Object,
      default: null,
    },
  },
  computed: {
    ...mapState("Viq", ["isStreaming", "streamingText"]),
    parsedResults() {
      return this.results || null;
    },
    gapItems() {
      if (!this.parsedResults) return [];
      return this.parsedResults.gaps || this.parsedResults.items || [];
    },
    renderedContent() {
      const text = this.parsedResults?.content || this.streamingText || "";
      if (!text) return "";
      let html = text;
      html = html.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
      html = html.replace(/\*(.+?)\*/g, "<em>$1</em>");
      html = html.replace(/^[*-] (.+)$/gm, "<li>$1</li>");
      html = html.replace(/((?:<li>.*<\/li>\n?)+)/g, "<ul>$1</ul>");
      html = html.replace(/\n\n/g, "</p><p>");
      html = "<p>" + html + "</p>";
      html = html.replace(/<p><\/p>/g, "");
      html = html.replace(/\n/g, "<br>");
      return DOMPurify.sanitize(html);
    },
  },
  methods: {
    exportPdf() {
      this.$emit("export-pdf");
    },
    share() {
      this.$store.dispatch("Viq/openShareDialog", {
        type: "gap-analysis",
        results: this.results,
      });
    },
    newAnalysis() {
      this.$store.commit("Viq/SET_GAP_QUESTIONS", []);
      this.$store.commit("Viq/SET_GAP_RESULTS", null);
      this.$store.commit("Viq/SET_REPORT_STEP", 1);
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/viq.scss";

.viq-gap-results {
  padding: $viq-space-lg 0;

  &__title {
    font-size: $viq-font-lg;
    font-weight: 700;
    color: $viq-text-primary;
    margin: 0 0 $viq-space-lg;
  }

  &__loading {
    display: flex;
    align-items: center;
    gap: $viq-space-sm;
    padding: $viq-space-lg;
    justify-content: center;
    color: $viq-text-secondary;
  }

  &__spinner {
    width: 16px;
    height: 16px;
    border: 2px solid $viq-border;
    border-top-color: $viq-accent;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  &__summary {
    padding: $viq-space-lg;
    background: $viq-citation-bg;
    border-radius: $viq-radius-md;
    margin-bottom: $viq-space-lg;

    h4 {
      font-size: $viq-font-md;
      font-weight: 700;
      color: $viq-text-primary;
      margin: 0 0 $viq-space-sm;
    }

    p {
      font-size: $viq-font-base;
      color: $viq-text-primary;
      margin: 0;
      line-height: 1.6;
    }
  }

  &__items {
    display: flex;
    flex-direction: column;
    gap: $viq-space-md;
    margin-bottom: $viq-space-lg;
  }

  &__item {
    padding: $viq-space-lg;
    background: $viq-bg-primary;
    border: 1px solid $viq-border;
    border-radius: $viq-radius-md;

    &-header {
      display: flex;
      align-items: center;
      gap: $viq-space-sm;
      margin-bottom: $viq-space-sm;
    }

    &-title {
      font-size: $viq-font-base;
      font-weight: 700;
      color: $viq-text-primary;
      margin: 0;
    }

    &-desc {
      font-size: $viq-font-sm;
      color: $viq-text-secondary;
      margin: 0 0 $viq-space-sm;
      line-height: 1.5;
    }
  }

  &__severity {
    flex-shrink: 0;
    padding: 2px 8px;
    border-radius: $viq-radius-full;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;

    &--high, &--critical {
      background: rgba(239, 68, 68, 0.1);
      color: $viq-error;
    }
    &--medium {
      background: rgba(245, 158, 11, 0.1);
      color: $viq-warning;
    }
    &--low {
      background: rgba(16, 185, 129, 0.1);
      color: $viq-success;
    }
  }

  &__recommendation {
    padding: $viq-space-sm $viq-space-md;
    background: $viq-bg-secondary;
    border-radius: $viq-radius-sm;
    font-size: $viq-font-sm;
    color: $viq-text-primary;
    line-height: 1.5;
  }

  &__content {
    font-size: $viq-font-base;
    line-height: 1.7;
    color: $viq-text-primary;
    margin-bottom: $viq-space-lg;

    ::v-deep {
      p { margin: 0 0 $viq-space-sm; }
      ul { padding-left: $viq-space-lg; }
      strong { font-weight: 700; }
    }
  }

  &__actions {
    display: flex;
    gap: $viq-space-sm;
  }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
